import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MDButton from "components/MDButton";
import { useSelector, useDispatch } from "react-redux";
import { setOpenPopup, setEditPopup } from "features/alert/alertSlice";
import { editAlertConfiguration } from "config/apis";
import { fetchAlertConfigDetails } from "features/alert/alertThunk";

const EditAlert = () => {
  const dispatch = useDispatch();
  const selectedAlert = useSelector((state) => state.alerts.selectedAlert);
  const editPopup = useSelector((state) => state.alerts.editPopup);

  const ignitionAlertIds = ["14", "9", "6", "10", "7", "8"];

  const isIgnitionAlert = ignitionAlertIds.includes(
    String(selectedAlert?.alertId)
  );

  const isDateAlert = String(selectedAlert?.alertId) === "15";

  const [formData, setFormData] = useState({
    alertValue: "",
    mobileNumber: [],
    emails: [],
    startTime: "",
    endTime: "",
  });

  const [emailInput, setEmailInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ---------------- Validation Functions ---------------- */

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidMobile = (mobile) => {
    const mobileRegex = /^[0-9]{10}$/;
    return mobileRegex.test(mobile);
  };

  /* ---------------- Populate Existing Data ---------------- */

  useEffect(() => {
    if (selectedAlert) {
      setFormData({
        alertValue: selectedAlert.alertValue || "",
        mobileNumber: Array.isArray(selectedAlert.mobileNumber)
          ? selectedAlert.mobileNumber
          : [],
        emails: Array.isArray(selectedAlert.emailId)
          ? selectedAlert.emailId
          : [],
        startTime: selectedAlert.startTime || "",
        endTime: selectedAlert.endTime || "",
      });
    }
  }, [selectedAlert]);

  const handleClose = () => dispatch(setEditPopup(false));

  /* ---------------- Add / Delete Mobile ---------------- */

  const addMobile = () => {
    const trimmedMobile = mobileInput.trim();

    if (!trimmedMobile) return;

    if (!isValidMobile(trimmedMobile)) {
      setSnackbar({
        open: true,
        message: "Mobile number must be 10 digits",
        severity: "error",
      });
      return;
    }

    if (formData.mobileNumber.includes(trimmedMobile)) {
      setSnackbar({
        open: true,
        message: "Mobile number already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      mobileNumber: [...formData.mobileNumber, trimmedMobile],
    });

    setMobileInput("");
  };

  const handleAddMobile = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addMobile();
    }
  };

  const handleDeleteMobile = (mobileToDelete) => {
    setFormData({
      ...formData,
      mobileNumber: formData.mobileNumber.filter(
        (num) => num !== mobileToDelete
      ),
    });
  };

  /* ---------------- Add / Delete Email ---------------- */

  const addEmail = () => {
    const trimmedEmail = emailInput.trim();

    if (!trimmedEmail) return;

    if (!isValidEmail(trimmedEmail)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid Email ID",
        severity: "error",
      });
      return;
    }

    if (formData.emails.includes(trimmedEmail)) {
      setSnackbar({
        open: true,
        message: "Email already added",
        severity: "warning",
      });
      return;
    }

    setFormData({
      ...formData,
      emails: [...formData.emails, trimmedEmail],
    });

    setEmailInput("");
  };

  const handleAddEmail = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleDeleteEmail = (emailToDelete) => {
    setFormData({
      ...formData,
      emails: formData.emails.filter((email) => email !== emailToDelete),
    });
  };

  /* ---------------- Submit ---------------- */

  const handleFormSubmit = async () => {
    const token = localStorage.getItem("token");

   if (!isIgnitionAlert && !formData.alertValue) {
  setSnackbar({
    open: true,
    message: isDateAlert ? "Please select a date" : "Alert value is required",
    severity: "warning",
  });
  return;
}

    const { startTime, endTime } = formData;

    if ((startTime && !endTime) || (!startTime && endTime)) {
      setSnackbar({
        open: true,
        message: "Please provide both Start Time and End Time, or leave both empty.",
        severity: "error",
      });
      return;
    }

    const payload = [
      {
        thingId: selectedAlert?.thingId || "",
        mobileNumber: formData.mobileNumber,
        emailId: formData.emails,
        alertValue: isIgnitionAlert ? "" : formData.alertValue,
        alertID: selectedAlert?.alertId || "",
        alertEnabled: "true",
        alertValueOption: "true",
        startTime: isDateAlert ? "" : formData.startTime,
        endTime: isDateAlert ? "" : formData.endTime,
      },
    ];

    try {
      const response = await fetch(editAlertConfiguration, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-dhundhoo-session": token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to update alert");

      const result = await response.json();

      const apiMessage =
        result?.responseData?.[0]?.message ||
        "Alert updated successfully";

      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "success",
      });

      dispatch(setOpenPopup(false));
      await dispatch(fetchAlertConfigDetails(selectedAlert?.alertId));
      setFormData({ alertValue: "", mobileNumber: [], emails: [] });
      handleClose();
    } catch (error) {
      console.error("❌ Error updating alert:", error);

      setSnackbar({
        open: true,
        message: "Failed to update alert",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Dialog
        open={editPopup}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", boxShadow: 8 },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#3177e8",
          }}
        >
          <Typography variant="h6" color="white" fontWeight="bold">
            Edit Alert Configuration
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
         {isDateAlert ? (
  <TextField
    label="Select Date"
    type="date"
    value={formData.alertValue}
    onChange={(e) =>
      setFormData({ ...formData, alertValue: e.target.value })
    }
    fullWidth
    InputLabelProps={{ shrink: true }}
    required
  />
) : !isIgnitionAlert ? (
  <TextField
    label="Alert Value *"
    value={formData.alertValue}
    onChange={(e) =>
      setFormData({ ...formData, alertValue: e.target.value })
    }
    required
    fullWidth
  />
) : null}

          {/* Mobile */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                label="Add Mobile Number"
                value={mobileInput}
                onChange={(e) =>
                  setMobileInput(e.target.value.replace(/\D/g, "")) // Only digits
                }
                onKeyDown={handleAddMobile}
                inputProps={{ maxLength: 10 }}
                fullWidth
              />
              <IconButton
                onClick={addMobile}
                sx={{
                  bgcolor: "#2E7BEF",
                  color: "white",
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  "&:hover": { bgcolor: "#1a5ed8" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.mobileNumber.map((num, idx) => (
                <Chip
                  key={idx}
                  label={num}
                  onDelete={() => handleDeleteMobile(num)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {/* Email */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                label="Add Email ID"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleAddEmail}
                fullWidth
              />
              <IconButton
                onClick={addEmail}
                sx={{
                  bgcolor: "#2E7BEF",
                  color: "white",
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  "&:hover": { bgcolor: "#1a5ed8" },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {formData.emails.map((email, idx) => (
                <Chip
                  key={idx}
                  label={email}
                  onDelete={() => handleDeleteEmail(email)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

         {!isDateAlert && (
  <Box sx={{ display: "flex", gap: 2 }}>
    <TextField
      label="Start Time"
      type="time"
      value={formData.startTime}
      onChange={(e) =>
        setFormData({ ...formData, startTime: e.target.value })
      }
      fullWidth
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      label="End Time"
      type="time"
      value={formData.endTime}
      onChange={(e) =>
        setFormData({ ...formData, endTime: e.target.value })
      }
      fullWidth
      InputLabelProps={{ shrink: true }}
    />
  </Box>
)}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <MDButton onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleFormSubmit} color="info" variant="contained">
            Update
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditAlert;